import { Box, Card, Portal, ResponsiveValue, Spinner } from '@chakra-ui/react';
import { useRef, useEffect, MutableRefObject, PropsWithChildren } from 'react';
import * as CSS from 'csstype';

import { useLayout } from './layout';

type DropdownProps = PropsWithChildren & {
  open: boolean
  fieldRef: MutableRefObject<HTMLDivElement>
  setOpen(value: boolean): void
  autoWidth?: boolean
  overflowY?: ResponsiveValue<CSS.Property.OverflowY>
}

export const Dropdown = (props: DropdownProps) => {
  const {
    open,
    fieldRef,
    setOpen,
    children,
    autoWidth,
    overflowY = 'auto',
  } = props;
  const { containerRef } = useLayout();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!fieldRef.current || !dropdownRef.current) {
      return;
    }
    const scrollX = containerRef.current.scrollLeft;
    const scrollY = containerRef.current.scrollTop;
    const containerRect = containerRef.current.getBoundingClientRect();
    const fieldRect = fieldRef.current.getBoundingClientRect();
    const dropdownRect = dropdownRef.current.getBoundingClientRect();
    
    if (!autoWidth) {
      dropdownRef.current.style.width = `${fieldRect.width}px`;
    }
    dropdownRef.current.style.left = `${fieldRect.x - containerRef.current?.offsetLeft + scrollX}px`;

    const topPosition = fieldRect.y + fieldRect.height + scrollY - containerRect.y;
    const openUp = topPosition + dropdownRect.height > window.innerHeight;
    if (openUp) {
      dropdownRef.current.style.top = `${topPosition - fieldRect.height - dropdownRect.height}px`;
      return;
    }

    dropdownRef.current.style.top = `${topPosition}px`;
    dropdownRef.current.style.maxHeight = `${window.innerHeight - topPosition}px`;
  }, [open, autoWidth, fieldRef, containerRef]);

  useEffect(() => {
    const onResize = () => {
      setOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  });

  return <Portal containerRef={containerRef}>
    <Card ref={dropdownRef} 
      boxShadow='xl' border='1px solid var(--chakra-colors-gray-200)' 
      display={open ? 'block' : 'none'} position='absolute' 
      zIndex={9999} overflowX='hidden' overflowY={overflowY}
      maxHeight={500}
    >
      {children}
    </Card>
  </Portal>;
};

const Option = ({ children, selected, hovered, onClick }: { selected?: boolean, hovered?: boolean, onClick(): void } & PropsWithChildren) => {
  return <Box px={4} py={2}
    _hover={{
      backgroundColor: 'gray.100',
    }}
    backgroundColor={selected ? 'gray.200' : hovered ? 'gray.100' : 'unset'}
    cursor='pointer'
    onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      onClick();
    }}
  >
    {children}
  </Box>;
};

const Loader = ({ label = 'Поиск...' }: { label?: string }) => {
  return <Box px={4} py={2}
    cursor='pointer'
    display='flex'
    gap={2}
    alignItems='center'
  >
    <Spinner
      thickness='2px'
      speed='0.65s'
      emptyColor='gray.200'
      color='blue.500'
      size='sm'
    />
    {label}
  </Box>;
};

const Label = ({ label = 'Пусто' }: { label?: string }) => {
  return <Box px={4} py={2}
    cursor='pointer'
    display='flex'
    gap={2}
    color='gray.400'
  >
    {label}
  </Box>;
};

Dropdown.Option = Option;
Dropdown.Loader = Loader;
Dropdown.Label = Label;
